import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Package,
  Pencil,
  Plus,
  RefreshCw,
  Trash2,
  X,
} from 'lucide-react';
import { useEffect, useState, type FormEvent } from 'react';

import { ApiError, apiFetch } from '@/lib/api';
import { formatBRL } from '@/lib/utils';

// ============================ types ============================

interface ServiceItem {
  id: string;
  codigo: string;
  nome: string;
  subtitulo: string | null;
  precoBase: string | number | null;
  precoUnidade: string | null;
  isActive: boolean;
  validadeDiasDefault: number;
}

type ChargeLineKind = 'UNICA' | 'MENSAL' | 'ANUAL';

interface ChargeLine {
  descricao: string;
  valor: number;
  tipo: ChargeLineKind;
  notaCalculo?: string;
}

interface ServiceFull extends ServiceItem {
  pitch: string | null;
  problemTitulo: string | null;
  problemCorpo: string | null;
  stats: Array<{ valor: string; unidade?: string; descricao: string }> | null;
  features: Array<{ numero?: number; icon?: string; titulo: string; descricao: string }> | null;
  highlightNumber: string | null;
  highlightUnit: string | null;
  highlightTitulo: string | null;
  highlightCorpo: string | null;
  benefits: Array<{ titulo: string; descricao: string }> | null;
  comparison: {
    colunas: string[];
    linhas: string[][];
    destaqueColuna?: number;
  } | null;
  included: Array<{ titulo: string; descricao?: string }> | null;
  precoNota: string | null;
  precoSubnota: string | null;
  nextSteps: Array<{ titulo: string; descricao: string }> | null;
  ctaPergunta: string | null;
  ctaResposta: string | null;
  contatoNome: string | null;
  contatoWhatsapp: string | null;
  contatoEmail: string | null;
  chargeLines: ChargeLine[] | null;
  issuerCompanyId: string | null;
}

interface FormState {
  codigo: string;
  nome: string;
  subtitulo: string;
  pitch: string;
  problemTitulo: string;
  problemCorpo: string;
  stats: Array<{ valor: string; unidade: string; descricao: string }>;
  features: Array<{ icon: string; titulo: string; descricao: string }>;
  highlightNumber: string;
  highlightUnit: string;
  highlightTitulo: string;
  highlightCorpo: string;
  benefits: Array<{ titulo: string; descricao: string }>;
  comparisonHeaders: string;
  comparisonRows: string;
  included: Array<{ titulo: string; descricao: string }>;
  precoBase: number;
  precoUnidade: string;
  precoNota: string;
  precoSubnota: string;
  nextSteps: Array<{ titulo: string; descricao: string }>;
  ctaPergunta: string;
  ctaResposta: string;
  contatoNome: string;
  contatoWhatsapp: string;
  contatoEmail: string;
  validadeDiasDefault: number;
  isActive: boolean;
  chargeLines: Array<{
    descricao: string;
    valor: number;
    tipo: ChargeLineKind;
    notaCalculo: string;
  }>;
  issuerCompanyId: string;
}

const emptyForm = (): FormState => ({
  codigo: '',
  nome: '',
  subtitulo: '',
  pitch: '',
  problemTitulo: '',
  problemCorpo: '',
  stats: [{ valor: '', unidade: '', descricao: '' }],
  features: [{ icon: '', titulo: '', descricao: '' }],
  highlightNumber: '',
  highlightUnit: '',
  highlightTitulo: '',
  highlightCorpo: '',
  benefits: [{ titulo: '', descricao: '' }],
  comparisonHeaders: '',
  comparisonRows: '',
  included: [{ titulo: '', descricao: '' }],
  precoBase: 0,
  precoUnidade: '/mês',
  precoNota: '',
  precoSubnota: '',
  nextSteps: [{ titulo: '', descricao: '' }],
  ctaPergunta: '',
  ctaResposta: '',
  contatoNome: '',
  contatoWhatsapp: '',
  contatoEmail: '',
  validadeDiasDefault: 30,
  isActive: true,
  chargeLines: [],
  issuerCompanyId: '',
});

function serviceToForm(s: ServiceFull): FormState {
  const compHeaders = s.comparison?.colunas?.join(';') ?? '';
  const compRows = s.comparison?.linhas?.map((r) => r.join(';')).join('\n') ?? '';
  return {
    codigo: s.codigo,
    nome: s.nome,
    subtitulo: s.subtitulo ?? '',
    pitch: s.pitch ?? '',
    problemTitulo: s.problemTitulo ?? '',
    problemCorpo: s.problemCorpo ?? '',
    stats:
      s.stats?.map((x) => ({
        valor: x.valor,
        unidade: x.unidade ?? '',
        descricao: x.descricao,
      })) ?? [{ valor: '', unidade: '', descricao: '' }],
    features:
      s.features?.map((x) => ({
        icon: x.icon ?? '',
        titulo: x.titulo,
        descricao: x.descricao,
      })) ?? [{ icon: '', titulo: '', descricao: '' }],
    highlightNumber: s.highlightNumber ?? '',
    highlightUnit: s.highlightUnit ?? '',
    highlightTitulo: s.highlightTitulo ?? '',
    highlightCorpo: s.highlightCorpo ?? '',
    benefits: s.benefits ?? [{ titulo: '', descricao: '' }],
    comparisonHeaders: compHeaders,
    comparisonRows: compRows,
    included:
      s.included?.map((x) => ({
        titulo: x.titulo,
        descricao: x.descricao ?? '',
      })) ?? [{ titulo: '', descricao: '' }],
    precoBase: s.precoBase ? Number(s.precoBase) : 0,
    precoUnidade: s.precoUnidade ?? '/mês',
    precoNota: s.precoNota ?? '',
    precoSubnota: s.precoSubnota ?? '',
    nextSteps: s.nextSteps ?? [{ titulo: '', descricao: '' }],
    ctaPergunta: s.ctaPergunta ?? '',
    ctaResposta: s.ctaResposta ?? '',
    contatoNome: s.contatoNome ?? '',
    contatoWhatsapp: s.contatoWhatsapp ?? '',
    contatoEmail: s.contatoEmail ?? '',
    validadeDiasDefault: s.validadeDiasDefault,
    isActive: s.isActive,
    chargeLines:
      s.chargeLines?.map((c) => ({
        descricao: c.descricao,
        valor: Number(c.valor),
        tipo: c.tipo,
        notaCalculo: c.notaCalculo ?? '',
      })) ?? [],
    issuerCompanyId: s.issuerCompanyId ?? '',
  };
}

function formToPayload(f: FormState) {
  const compCols = f.comparisonHeaders
    .split(';')
    .map((s) => s.trim())
    .filter(Boolean);
  const compRows = f.comparisonRows
    .split('\n')
    .map((line) => line.split(';').map((s) => s.trim()))
    .filter((row) => row.some(Boolean));
  return {
    codigo: f.codigo,
    nome: f.nome,
    subtitulo: f.subtitulo || undefined,
    pitch: f.pitch || undefined,
    problemTitulo: f.problemTitulo || undefined,
    problemCorpo: f.problemCorpo || undefined,
    stats: f.stats.filter((s) => s.descricao || s.valor).map((s) => ({
      valor: s.valor,
      unidade: s.unidade || undefined,
      descricao: s.descricao,
    })),
    features: f.features.filter((s) => s.titulo).map((s) => ({
      icon: s.icon || undefined,
      titulo: s.titulo,
      descricao: s.descricao,
    })),
    highlightNumber: f.highlightNumber || undefined,
    highlightUnit: f.highlightUnit || undefined,
    highlightTitulo: f.highlightTitulo || undefined,
    highlightCorpo: f.highlightCorpo || undefined,
    benefits: f.benefits.filter((s) => s.titulo),
    comparison:
      compCols.length > 0
        ? { colunas: compCols, linhas: compRows }
        : undefined,
    included: f.included.filter((s) => s.titulo).map((s) => ({
      titulo: s.titulo,
      descricao: s.descricao || undefined,
    })),
    precoBase: f.precoBase || undefined,
    precoUnidade: f.precoUnidade || undefined,
    precoNota: f.precoNota || undefined,
    precoSubnota: f.precoSubnota || undefined,
    nextSteps: f.nextSteps.filter((s) => s.titulo),
    ctaPergunta: f.ctaPergunta || undefined,
    ctaResposta: f.ctaResposta || undefined,
    contatoNome: f.contatoNome || undefined,
    contatoWhatsapp: f.contatoWhatsapp || undefined,
    contatoEmail: f.contatoEmail || undefined,
    validadeDiasDefault: f.validadeDiasDefault,
    isActive: f.isActive,
    chargeLines: f.chargeLines
      .filter((c) => c.descricao && c.valor !== 0)
      .map((c) => ({
        descricao: c.descricao,
        valor: c.valor,
        tipo: c.tipo,
        notaCalculo: c.notaCalculo || undefined,
      })),
    issuerCompanyId: f.issuerCompanyId || undefined,
  };
}

// ============================ page ============================

export function ServicesPage() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<ServiceFull | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ['services'],
    queryFn: () => apiFetch<ServiceItem[]>('/services'),
  });

  const remove = useMutation({
    mutationFn: (id: string) => apiFetch<void>(`/services/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['services'] }),
  });

  return (
    <div className="space-y-6">
      <header className="flex items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">Catálogo de Serviços</h1>
          <p className="text-sm text-muted-foreground">
            Cada serviço guarda o conteúdo fixo da proposta (texto, estatísticas,
            benefícios, preço). Ao criar uma proposta, você só preenche cliente e
            valor.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => refetch()}
            disabled={isFetching}
            className="flex items-center gap-1 rounded-md border border-input bg-background px-3 py-2 text-sm hover:bg-accent disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
          </button>
          <button
            type="button"
            onClick={() => setShowCreate(true)}
            className="flex items-center gap-1 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            <Plus className="h-4 w-4" /> Novo serviço
          </button>
        </div>
      </header>

      <section className="rounded-lg border border-border bg-card">
        {isLoading && <p className="p-4 text-sm text-muted-foreground">Carregando…</p>}
        {data && data.length === 0 && (
          <div className="flex flex-col items-center gap-3 p-12 text-center">
            <Package className="h-10 w-10 text-muted-foreground/50" />
            <p className="text-sm text-muted-foreground">Nenhum serviço cadastrado.</p>
          </div>
        )}
        {data && data.length > 0 && (
          <table className="w-full text-sm">
            <thead className="border-b border-border bg-muted/30 text-left">
              <tr>
                <th className="px-4 py-2 font-medium">Código</th>
                <th className="px-4 py-2 font-medium">Nome</th>
                <th className="px-4 py-2 font-medium">Preço base</th>
                <th className="px-4 py-2 font-medium">Validade</th>
                <th className="px-4 py-2 font-medium">Status</th>
                <th className="px-2 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {data.map((s) => (
                <tr key={s.id} className="border-b border-border last:border-0 hover:bg-accent/30">
                  <td className="px-4 py-2 font-mono text-xs">{s.codigo}</td>
                  <td className="px-4 py-2 font-medium">
                    {s.nome}
                    {s.subtitulo && (
                      <span className="block text-xs text-muted-foreground">{s.subtitulo}</span>
                    )}
                  </td>
                  <td className="px-4 py-2">
                    {s.precoBase ? formatBRL(Number(s.precoBase)) : '—'}
                    {s.precoUnidade && (
                      <span className="text-xs text-muted-foreground">{s.precoUnidade}</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-xs">{s.validadeDiasDefault} dias</td>
                  <td className="px-4 py-2">
                    <span
                      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs ${
                        s.isActive
                          ? 'bg-emerald-500/10 text-emerald-600'
                          : 'bg-muted text-muted-foreground'
                      }`}
                    >
                      {s.isActive ? 'Ativo' : 'Inativo'}
                    </span>
                  </td>
                  <td className="px-2 py-2 flex justify-end gap-1">
                    <button
                      type="button"
                      data-dica="Editar"
                      onClick={async () => {
                        const full = await apiFetch<ServiceFull>(`/services/${s.id}`);
                        setEditing(full);
                      }}
                      className="rounded-md p-2 text-muted-foreground hover:bg-accent"
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      data-dica="Excluir"
                      onClick={() => {
                        if (confirm(`Excluir serviço ${s.nome}?`)) {
                          remove.mutate(s.id);
                        }
                      }}
                      className="rounded-md p-2 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {showCreate && (
        <ServiceFormDialog
          onClose={() => setShowCreate(false)}
          onSaved={() => {
            setShowCreate(false);
            qc.invalidateQueries({ queryKey: ['services'] });
          }}
        />
      )}
      {editing && (
        <ServiceFormDialog
          service={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            qc.invalidateQueries({ queryKey: ['services'] });
          }}
        />
      )}
    </div>
  );
}

// ============================ dialog ============================

function ServiceFormDialog({
  service,
  onClose,
  onSaved,
}: {
  service?: ServiceFull;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isEdit = !!service;
  const [form, setForm] = useState<FormState>(() =>
    service ? serviceToForm(service) : emptyForm(),
  );
  const [error, setError] = useState<string | null>(null);

  const issuers = useQuery({
    queryKey: ['issuer-companies'],
    queryFn: () =>
      apiFetch<Array<{ id: string; razaoSocial: string; isDefault: boolean }>>(
        '/issuer-companies',
      ),
  });

  useEffect(() => {
    if (service) setForm(serviceToForm(service));
  }, [service]);

  const save = useMutation({
    mutationFn: () => {
      const body = JSON.stringify(formToPayload(form));
      return isEdit
        ? apiFetch(`/services/${service!.id}`, { method: 'PATCH', body })
        : apiFetch('/services', { method: 'POST', body });
    },
    onSuccess: onSaved,
    onError: (err) =>
      setError(err instanceof ApiError ? err.body : err instanceof Error ? err.message : 'erro'),
  });

  function handleSubmit(ev: FormEvent) {
    ev.preventDefault();
    setError(null);
    save.mutate();
  }

  return (
    <Dialog title={isEdit ? `Editar serviço — ${service!.nome}` : 'Novo serviço'} onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Identidade */}
        <Section title="Identidade">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Código *">
              <input
                required
                className="input"
                value={form.codigo}
                onChange={(e) => setForm((s) => ({ ...s, codigo: e.target.value }))}
              />
            </Field>
            <Field label="Nome *">
              <input
                required
                className="input"
                value={form.nome}
                onChange={(e) => setForm((s) => ({ ...s, nome: e.target.value }))}
              />
            </Field>
            <Field label="Subtítulo">
              <input
                className="input"
                value={form.subtitulo}
                onChange={(e) => setForm((s) => ({ ...s, subtitulo: e.target.value }))}
              />
            </Field>
            <Field label="Validade padrão (dias)">
              <input
                type="number"
                min={1}
                className="input"
                value={form.validadeDiasDefault}
                onChange={(e) =>
                  setForm((s) => ({ ...s, validadeDiasDefault: Number(e.target.value) || 30 }))
                }
              />
            </Field>
            <Field label="Empresa emissora padrão">
              <select
                className="input"
                value={form.issuerCompanyId}
                onChange={(e) => setForm((s) => ({ ...s, issuerCompanyId: e.target.value }))}
              >
                <option value="">— Usar padrão da proposta —</option>
                {issuers.data?.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.razaoSocial}
                    {c.isDefault ? ' (padrão)' : ''}
                  </option>
                ))}
              </select>
            </Field>
          </div>
        </Section>

        {/* Hero */}
        <Section title="Hero (página 1)">
          <Field label="Pitch (descrição curta)">
            <textarea
              rows={2}
              className="input"
              value={form.pitch}
              onChange={(e) => setForm((s) => ({ ...s, pitch: e.target.value }))}
            />
          </Field>
          <Field label="Título do problema (citação)">
            <input
              className="input"
              value={form.problemTitulo}
              onChange={(e) => setForm((s) => ({ ...s, problemTitulo: e.target.value }))}
            />
          </Field>
          <Field label="Corpo do problema">
            <textarea
              rows={2}
              className="input"
              value={form.problemCorpo}
              onChange={(e) => setForm((s) => ({ ...s, problemCorpo: e.target.value }))}
            />
          </Field>
        </Section>

        {/* Stats */}
        <Section title="Estatísticas (3 cards)">
          <DynamicList
            items={form.stats}
            onChange={(stats) => setForm((s) => ({ ...s, stats }))}
            empty={{ valor: '', unidade: '', descricao: '' }}
            render={(item, onItem) => (
              <div className="grid grid-cols-[1fr_60px_2fr] gap-2">
                <input
                  className="input"
                  placeholder="Valor (ex.: 60)"
                  value={item.valor}
                  onChange={(e) => onItem({ ...item, valor: e.target.value })}
                />
                <input
                  className="input"
                  placeholder="%"
                  value={item.unidade}
                  onChange={(e) => onItem({ ...item, unidade: e.target.value })}
                />
                <input
                  className="input"
                  placeholder="Descrição"
                  value={item.descricao}
                  onChange={(e) => onItem({ ...item, descricao: e.target.value })}
                />
              </div>
            )}
          />
        </Section>

        {/* Features */}
        <Section title="Como funciona (4 cards)">
          <DynamicList
            items={form.features}
            onChange={(features) => setForm((s) => ({ ...s, features }))}
            empty={{ icon: '', titulo: '', descricao: '' }}
            render={(item, onItem) => (
              <div className="grid grid-cols-[50px_1fr_2fr] gap-2">
                <input
                  className="input"
                  placeholder="🛡"
                  value={item.icon}
                  onChange={(e) => onItem({ ...item, icon: e.target.value })}
                />
                <input
                  className="input"
                  placeholder="Título"
                  value={item.titulo}
                  onChange={(e) => onItem({ ...item, titulo: e.target.value })}
                />
                <input
                  className="input"
                  placeholder="Descrição"
                  value={item.descricao}
                  onChange={(e) => onItem({ ...item, descricao: e.target.value })}
                />
              </div>
            )}
          />
        </Section>

        {/* Callout */}
        <Section title="Destaque (página 2 — ex.: 30 DIAS)">
          <div className="grid grid-cols-[100px_100px_1fr] gap-2">
            <Field label="Número">
              <input
                className="input"
                placeholder="30"
                value={form.highlightNumber}
                onChange={(e) => setForm((s) => ({ ...s, highlightNumber: e.target.value }))}
              />
            </Field>
            <Field label="Unidade">
              <input
                className="input"
                placeholder="DIAS"
                value={form.highlightUnit}
                onChange={(e) => setForm((s) => ({ ...s, highlightUnit: e.target.value }))}
              />
            </Field>
            <Field label="Título">
              <input
                className="input"
                value={form.highlightTitulo}
                onChange={(e) => setForm((s) => ({ ...s, highlightTitulo: e.target.value }))}
              />
            </Field>
          </div>
          <Field label="Corpo do destaque">
            <textarea
              rows={2}
              className="input"
              value={form.highlightCorpo}
              onChange={(e) => setForm((s) => ({ ...s, highlightCorpo: e.target.value }))}
            />
          </Field>
        </Section>

        {/* Benefits */}
        <Section title="Benefícios (grid 2 colunas)">
          <DynamicList
            items={form.benefits}
            onChange={(benefits) => setForm((s) => ({ ...s, benefits }))}
            empty={{ titulo: '', descricao: '' }}
            render={(item, onItem) => (
              <div className="grid grid-cols-[1fr_2fr] gap-2">
                <input
                  className="input"
                  placeholder="Título"
                  value={item.titulo}
                  onChange={(e) => onItem({ ...item, titulo: e.target.value })}
                />
                <input
                  className="input"
                  placeholder="Descrição"
                  value={item.descricao}
                  onChange={(e) => onItem({ ...item, descricao: e.target.value })}
                />
              </div>
            )}
          />
        </Section>

        {/* Comparison */}
        <Section title="Tabela comparativa">
          <Field label="Colunas (separadas por ;)">
            <input
              className="input"
              placeholder="Característica;HD/Pendrive;Backup manual;Backup SimpleHotel"
              value={form.comparisonHeaders}
              onChange={(e) => setForm((s) => ({ ...s, comparisonHeaders: e.target.value }))}
            />
          </Field>
          <Field label="Linhas (uma por linha; células com ;)">
            <textarea
              rows={5}
              className="input font-mono text-xs"
              placeholder="Automatização total;✗;✗;✓"
              value={form.comparisonRows}
              onChange={(e) => setForm((s) => ({ ...s, comparisonRows: e.target.value }))}
            />
          </Field>
        </Section>

        {/* Included */}
        <Section title="O que está incluído">
          <DynamicList
            items={form.included}
            onChange={(included) => setForm((s) => ({ ...s, included }))}
            empty={{ titulo: '', descricao: '' }}
            render={(item, onItem) => (
              <div className="grid grid-cols-[1fr_2fr] gap-2">
                <input
                  className="input"
                  placeholder="Título (ex.: Backup diário)"
                  value={item.titulo}
                  onChange={(e) => onItem({ ...item, titulo: e.target.value })}
                />
                <input
                  className="input"
                  placeholder="Descrição (opcional)"
                  value={item.descricao}
                  onChange={(e) => onItem({ ...item, descricao: e.target.value })}
                />
              </div>
            )}
          />
        </Section>

        {/* Linhas de cobrança */}
        <Section title="Linhas de cobrança (opcional)">
          <p className="text-xs text-muted-foreground">
            Quando preenchidas, substituem o "preço base" no PDF. Útil para
            implantação avulsa + mensalidade + taxa de manutenção.
          </p>
          <DynamicList
            items={form.chargeLines}
            onChange={(chargeLines) => setForm((s) => ({ ...s, chargeLines }))}
            empty={{ descricao: '', valor: 0, tipo: 'MENSAL' as ChargeLineKind, notaCalculo: '' }}
            render={(item, onItem) => (
              <div className="space-y-2">
                <div className="grid grid-cols-[2fr_100px_120px] gap-2">
                  <input
                    className="input"
                    placeholder="Descrição (ex.: Implantação, Manutenção…)"
                    value={item.descricao}
                    onChange={(e) => onItem({ ...item, descricao: e.target.value })}
                  />
                  <input
                    type="number"
                    step="0.01"
                    className={`input ${item.valor < 0 ? 'text-destructive font-medium' : ''}`}
                    placeholder="Valor"
                    value={item.valor || ''}
                    onChange={(e) => onItem({ ...item, valor: Number(e.target.value) || 0 })}
                  />
                  <select
                    className="input"
                    value={item.tipo}
                    onChange={(e) => onItem({ ...item, tipo: e.target.value as ChargeLineKind })}
                  >
                    <option value="UNICA">única</option>
                    <option value="MENSAL">/mês</option>
                    <option value="ANUAL">/ano</option>
                  </select>
                </div>
                <input
                  className="input"
                  placeholder="Nota de cálculo (ex.: 10% sobre mensalidade)"
                  value={item.notaCalculo}
                  onChange={(e) => onItem({ ...item, notaCalculo: e.target.value })}
                />
              </div>
            )}
          />
        </Section>

        {/* Preço */}
        <Section title="Investimento — preço base (legado, usado se não houver linhas)">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Preço base">
              <input
                type="number"
                step="0.01"
                min={0}
                className="input"
                value={form.precoBase || ''}
                onChange={(e) => setForm((s) => ({ ...s, precoBase: Number(e.target.value) || 0 }))}
              />
            </Field>
            <Field label="Unidade (ex.: /mês)">
              <input
                className="input"
                value={form.precoUnidade}
                onChange={(e) => setForm((s) => ({ ...s, precoUnidade: e.target.value }))}
              />
            </Field>
            <Field label="Nota (ex.: Sem custo de instalação)">
              <input
                className="input"
                value={form.precoNota}
                onChange={(e) => setForm((s) => ({ ...s, precoNota: e.target.value }))}
              />
            </Field>
            <Field label="Subnota (ex.: Inclui...)">
              <input
                className="input"
                value={form.precoSubnota}
                onChange={(e) => setForm((s) => ({ ...s, precoSubnota: e.target.value }))}
              />
            </Field>
          </div>
        </Section>

        {/* Próximos passos */}
        <Section title="Próximos passos">
          <DynamicList
            items={form.nextSteps}
            onChange={(nextSteps) => setForm((s) => ({ ...s, nextSteps }))}
            empty={{ titulo: '', descricao: '' }}
            render={(item, onItem) => (
              <div className="grid grid-cols-[1fr_2fr] gap-2">
                <input
                  className="input"
                  placeholder="Título do passo"
                  value={item.titulo}
                  onChange={(e) => onItem({ ...item, titulo: e.target.value })}
                />
                <input
                  className="input"
                  placeholder="Descrição"
                  value={item.descricao}
                  onChange={(e) => onItem({ ...item, descricao: e.target.value })}
                />
              </div>
            )}
          />
        </Section>

        {/* CTA + contatos */}
        <Section title="CTA final + contatos">
          <Field label="Pergunta CTA">
            <input
              className="input"
              value={form.ctaPergunta}
              onChange={(e) => setForm((s) => ({ ...s, ctaPergunta: e.target.value }))}
            />
          </Field>
          <Field label="Resposta/seguimento CTA">
            <input
              className="input"
              value={form.ctaResposta}
              onChange={(e) => setForm((s) => ({ ...s, ctaResposta: e.target.value }))}
            />
          </Field>
          <Field label="Nome do responsável comercial">
            <input
              className="input"
              placeholder="ex.: Jorge Rodrigues"
              value={form.contatoNome}
              onChange={(e) => setForm((s) => ({ ...s, contatoNome: e.target.value }))}
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="WhatsApp">
              <input
                className="input"
                value={form.contatoWhatsapp}
                onChange={(e) => setForm((s) => ({ ...s, contatoWhatsapp: e.target.value }))}
              />
            </Field>
            <Field label="Email">
              <input
                className="input"
                value={form.contatoEmail}
                onChange={(e) => setForm((s) => ({ ...s, contatoEmail: e.target.value }))}
              />
            </Field>
          </div>
        </Section>

        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={form.isActive}
            onChange={(e) => setForm((s) => ({ ...s, isActive: e.target.checked }))}
          />
          Ativo (aparece no select de Nova Proposta)
        </label>

        {error && (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        )}

        <div className="sticky bottom-0 -mx-6 -mb-6 flex justify-end gap-2 border-t border-border bg-card px-6 py-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-input px-3 py-2 text-sm hover:bg-accent"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={save.isPending}
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {save.isPending ? 'Salvando…' : isEdit ? 'Salvar alterações' : 'Criar serviço'}
          </button>
        </div>
      </form>

      <Style />
    </Dialog>
  );
}

// ============================ helpers ============================

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <fieldset className="space-y-3 rounded-md border border-border p-4">
      <legend className="px-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
        {title}
      </legend>
      {children}
    </fieldset>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="space-y-1 block">
      <span className="text-xs font-medium">{label}</span>
      {children}
    </label>
  );
}

function DynamicList<T>({
  items,
  onChange,
  empty,
  render,
}: {
  items: T[];
  onChange: (items: T[]) => void;
  empty: T;
  render: (item: T, onItem: (i: T) => void) => React.ReactNode;
}) {
  return (
    <div className="space-y-2">
      {items.map((item, idx) => (
        <div key={idx} className="flex gap-2 items-start">
          <div className="flex-1">
            {render(item, (next) => {
              const copy = [...items];
              copy[idx] = next;
              onChange(copy);
            })}
          </div>
          <button
            type="button"
            onClick={() => {
              const copy = [...items];
              copy.splice(idx, 1);
              onChange(copy.length ? copy : [empty]);
            }}
            className="rounded-md border border-input p-2 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={() => onChange([...items, empty])}
        className="flex items-center gap-1 rounded-md border border-dashed border-input px-3 py-1.5 text-xs text-muted-foreground hover:bg-accent"
      >
        <Plus className="h-3.5 w-3.5" /> Adicionar
      </button>
    </div>
  );
}

function Dialog({
  title,
  children,
  onClose,
}: {
  title: string;
  children: React.ReactNode;
  onClose: () => void;
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
    >
      <div
        className="max-h-[92vh] w-full max-w-4xl overflow-y-auto rounded-lg border border-border bg-card p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-medium">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-muted-foreground hover:bg-accent"
          >
            ✕
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Style() {
  return (
    <style>{`
      .input {
        width: 100%;
        border: 1px solid hsl(var(--input));
        background: hsl(var(--background));
        padding: 0.5rem 0.75rem;
        border-radius: 0.375rem;
        font-size: 0.875rem;
      }
      .input:focus { outline: none; box-shadow: 0 0 0 2px hsl(var(--ring) / 0.4); }
    `}</style>
  );
}
